import { useQuery } from '@tanstack/react-query';
import type { HeaderConfig, TimelineLayoutDTO } from '@tempo-io/roadmap-dto';
import { useMemo } from 'react';
import type { ItemV2 } from '@/lib/api/types/items';
import type {
  ItemStatusOption,
  ItemTypeLevelOption,
  ItemTypeOption,
} from '@/lib/api/types/loop-visualization';
import type { Team } from '@/lib/api/types/teams';
import { useTimelineHeaderConfig } from '@/lib/queries/loop-visualization/useTimelineHeaderConfig';
import { useTimelineLayout } from '@/lib/queries/loop-visualization/useTimelineLayout';
import { getVisualizationByTypeQueryOptions } from '@/lib/queries/loop-visualization/useVisualization';
import { headerToPropertySelector } from '@/lib/utils/loop-visualization/commonLayoutUtils';
import { annotateHiddenColorItems, useVisualizationColorData } from '../utils/visualization-colors';
import { useViewContext } from '../ViewContext';

export function useTimelineColorContext({
  items,
  itemTypes,
  itemLevels,
  itemStatuses,
  noValueLabel,
  isPublishedMode = false,
  publishedLayout,
  publishedMainHeaderConfig,
  allItemsById,
  teamsOverride,
}: {
  items: ItemV2[];
  itemTypes: ItemTypeOption[];
  itemLevels: ItemTypeLevelOption[];
  itemStatuses: ItemStatusOption[];
  noValueLabel: string;
  isPublishedMode?: boolean;
  publishedLayout?: TimelineLayoutDTO;
  publishedMainHeaderConfig?: HeaderConfig;
  allItemsById?: ReadonlyMap<number, Partial<ItemV2>>;
  teamsOverride?: Team[];
}) {
  const { viewId } = useViewContext();

  const { data: visualization } = useQuery({
    ...getVisualizationByTypeQueryOptions(viewId, 'timeline'),
    enabled: !isPublishedMode && !!viewId,
  });

  const visualizationId = isPublishedMode
    ? (publishedLayout?.visualizationId ?? 0)
    : (visualization?.visualizationId ?? 0);

  const { data: fetchedLayout } = useTimelineLayout(viewId, visualizationId, {
    enabled: !isPublishedMode,
  });

  const effectiveLayout = isPublishedMode ? publishedLayout : fetchedLayout;

  const { data: fetchedMainHeaderConfig } = useTimelineHeaderConfig({
    viewId,
    visualizationId,
    headerFieldId: isPublishedMode ? null : (effectiveLayout?.header1 ?? null),
  });

  const columnHeaderOrder = isPublishedMode
    ? publishedMainHeaderConfig?.orderedHeaderIds
    : fetchedMainHeaderConfig?.orderedHeaderIds;
  const rowHeaderOrder = effectiveLayout?.rowHeaderOrder;

  const mainHeaderHiddenIds = isPublishedMode
    ? publishedMainHeaderConfig?.hiddenHeaderIds
    : fetchedMainHeaderConfig?.hiddenHeaderIds;

  const effectiveHeaders = useMemo(() => {
    if (!effectiveLayout) return undefined;
    return {
      header1: headerToPropertySelector(effectiveLayout.header1),
      header2: headerToPropertySelector(effectiveLayout.header2),
      itemLevelId: effectiveLayout.itemLevelId,
    };
  }, [effectiveLayout]);

  const colorData = useVisualizationColorData({
    layout: effectiveLayout,
    headers: effectiveHeaders,
    items,
    itemTypes,
    itemLevels,
    itemStatuses,
    noValueLabel,
    columnHeaderOrder,
    rowHeaderOrder,
    enabled: !isPublishedMode,
    allItemsById,
    teamsOverride,
  });

  const annotatedColorItems = useMemo(
    () =>
      colorData.isColorByFieldAHeader
        ? annotateHiddenColorItems(colorData.colorItems, mainHeaderHiddenIds)
        : colorData.colorItems,
    [colorData.isColorByFieldAHeader, colorData.colorItems, mainHeaderHiddenIds],
  );

  return {
    viewId,
    visualizationId,
    layout: effectiveLayout,
    headers: effectiveHeaders,
    colorConfig: colorData.colorConfig,
    items: colorData.items,
    allItemsWithParents: colorData.allItemsWithParents,
    timelineHeader1: colorData.visualizationHeader1,
    timelineHeader2: colorData.visualizationHeader2,
    header1FieldId: colorData.header1FieldId,
    header2FieldId: colorData.header2FieldId,
    defaultColorByField: colorData.defaultColorByField,
    currentColorByField: colorData.currentColorByField,
    isBaseColorSelected: colorData.isBaseColorSelected,
    isColorByFieldAHeader: colorData.isColorByFieldAHeader,
    matchingHeader: colorData.matchingHeader,
    headerForColorField: colorData.headerForColorField,
    colorByFieldValues: colorData.colorByFieldValues,
    orderedHeaderValues: colorData.stableOrderedValues,
    currentHeaderColorOrder: colorData.currentHeaderColorOrder,
    paletteSwatches: colorData.paletteSwatches,
    labelMap: colorData.labelMap,
    colorItems: annotatedColorItems,
    valueToColorIndex: colorData.valueToColorIndex,
  };
}
